import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IcoChev, IcoSearch, IcoX } from '../bz/icons';
import {
  moreCount,
  rowProjects,
  searchProjects,
  selectionSummary,
  sortProjects,
  sourceLabel
} from './predictWatchlistModel';
import './predictProjectFilter.css';

const linkButton = {
  background: 'none',
  border: 0,
  padding: 0,
  color: 'var(--info)',
  cursor: 'pointer',
  font: 'inherit'
};

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

// "3 OSS", "1 weakness type": n options, in the filter's own words.
const counted = (n, noun, nouns) => `${n} ${n === 1 ? noun : nouns}`;

const projectShape = PropTypes.shape({
  target: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  source: PropTypes.string
});

// A chip that carries a count is named "Label, N bugs", so a screen reader
// hears the count in words rather than as a digit stuck to the label.
const Chip = ({ on, onClick, label, name, count }) => (
  <button
    type="button"
    className={`bz-btn bz-btn--sm pd-chip${on ? ' bz-btn--primary' : ''}`}
    aria-pressed={on}
    aria-label={`${name || label}, ${plural(count, 'bug')}`}
    onClick={onClick}
  >
    {label}
    <span className="bz-mono" style={{ opacity: 0.7, marginLeft: 6 }}>
      {count}
    </span>
  </button>
);
Chip.propTypes = {
  on: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  count: PropTypes.number.isRequired
};

const ProjectOption = ({ project, on, max, onToggle }) => (
  <label className={`pd-proj-row${on ? ' pd-proj-row--on' : ''}`}>
    <input
      type="checkbox"
      checked={on}
      aria-label={`${project.name}, ${plural(project.count, 'bug')}`}
      onChange={() => onToggle(project.target)}
    />
    <span className="bz-truncate">{project.name}</span>
    <span className="bz-muted pd-proj-row__source">{project.source ? sourceLabel(project.source) : ''}</span>
    <span className="bz-bar" aria-hidden="true">
      <span style={{ width: `${Math.round((project.count / max) * 100)}%` }} />
    </span>
    <span className="bz-mono pd-proj-row__count" aria-hidden="true">
      {project.count}
    </span>
  </label>
);
ProjectOption.propTypes = {
  project: projectShape.isRequired,
  on: PropTypes.bool.isRequired,
  max: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired
};

const ORDERS = [
  ['count', 'Most bugs', 'most bugs first'],
  ['name', 'A–Z', 'A–Z']
];

// Every project (or weakness type: `noun`) on the list, searchable, with the
// selected ones pinned on top. Focus starts in the search; Escape or a click
// outside closes it. It opens under its More chip, from the chip's left edge,
// or from its right edge when it would run past the filter row's end: the page
// scrolls inside a box that would cut it off.
const ProjectPicker = ({ id, noun, nouns, projects, selected, onToggle, onClear, onClose, toggleRef }) => {
  const count = n => counted(n, noun, nouns);
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState('count');
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const [alignEnd, setAlignEnd] = useState(false);

  // Before the first paint, so it never shows cut off and then jumps.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const row = panel?.closest('.pd-projects');
    if (row && panel.getBoundingClientRect().right > row.getBoundingClientRect().right) setAlignEnd(true);
  }, []);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const onPointerDown = event => {
      if (panelRef.current?.contains(event.target) || toggleRef.current?.contains(event.target)) return;
      onClose(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [onClose, toggleRef]);

  const onKeyDown = event => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    onClose(true);
  };

  const max = Math.max(1, ...projects.map(project => project.count));
  const chosen = sortProjects(
    projects.filter(project => selected.includes(project.target)),
    order
  );
  const others = searchProjects(
    sortProjects(
      projects.filter(project => !selected.includes(project.target)),
      order
    ),
    query
  );
  const orderLabel = ORDERS.find(([key]) => key === order)[2];

  return (
    <div
      id={id}
      ref={panelRef}
      className={`pd-picker${alignEnd ? ' pd-picker--end' : ''}`}
      role="dialog"
      aria-label={`All ${nouns}`}
      onKeyDown={onKeyDown}
    >
      <div className="pd-picker__head">
        <label className="bz-input" style={{ margin: 0 }}>
          <IcoSearch size={14} />
          <input
            ref={searchRef}
            type="search"
            value={query}
            placeholder={`Search ${count(projects.length)}`}
            aria-label={`Search ${nouns}`}
            onChange={event => setQuery(event.target.value)}
          />
          {query && (
            <button
              type="button"
              className="pl-search__clear"
              aria-label={`Clear the ${noun} search`}
              onClick={() => setQuery('')}
            >
              <IcoX size={12} />
            </button>
          )}
        </label>
        <div className="bz-row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <div className="pd-seg" role="group" aria-label={`Sort ${nouns}`}>
            {ORDERS.map(([key, label]) => (
              <button key={key} type="button" aria-pressed={order === key} onClick={() => setOrder(key)}>
                {label}
              </button>
            ))}
          </div>
          <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {selected.length} selected
          </span>
        </div>
      </div>

      <div className="pd-picker__list bz-scroll">
        {chosen.length > 0 && (
          <>
            <div className="bz-kicker pd-picker__group">Selected</div>
            {chosen.map(project => (
              <ProjectOption key={project.target} project={project} on max={max} onToggle={onToggle} />
            ))}
          </>
        )}
        <div className="bz-kicker pd-picker__group">
          All {nouns} · {orderLabel}
        </div>
        {others.map(project => (
          <ProjectOption key={project.target} project={project} on={false} max={max} onToggle={onToggle} />
        ))}
        {others.length === 0 && (
          <div className="bz-muted" style={{ padding: '7px 12px' }}>
            {query ? (
              <>
                No other {noun} matches &ldquo;{query}&rdquo;.
              </>
            ) : (
              `Every ${noun} is selected.`
            )}
          </div>
        )}
      </div>

      <div className="pd-picker__foot">
        <span className="bz-muted" data-testid="picker-count">
          Showing {chosen.length + others.length} of {projects.length}
        </span>
        <button type="button" className="bz-btn bz-btn--sm" onClick={onClear} disabled={!selected.length}>
          Clear
        </button>
      </div>
    </div>
  );
};
ProjectPicker.propTypes = {
  id: PropTypes.string.isRequired,
  noun: PropTypes.string.isRequired,
  nouns: PropTypes.string.isRequired,
  projects: PropTypes.arrayOf(projectShape).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  toggleRef: PropTypes.shape({ current: PropTypes.any }).isRequired
};

/**
 * The watch list's project filter (STRY0039087): "All", the projects with the
 * most bugs as chips, and the rest under "+N more", a searchable multi-select.
 * Chips toggle; several selected show the bugs of all of them. A project
 * selected under More joins the chips, so the selection is always in view.
 * Counts are the list's, at the threshold it was read at. `aside` sits at the
 * row's end.
 *
 * The weakness type filter (STRY0039127) is the same row: `title` names it,
 * `noun` and `nouns` are what one option and several are ("weakness type",
 * "weakness types"; the projects' are OSS, STRY0039119), `testId` prefixes its
 * summary's test id, and its `projects` are the types, {target, name, count}.
 */
const PredictProjectFilter = ({
  projects,
  total,
  selected,
  onChange,
  aside,
  title = 'OSS',
  noun = 'OSS',
  nouns = 'OSS',
  testId = 'project'
}) => {
  const [open, setOpen] = useState(false);
  const pickerId = useId();
  const toggleRef = useRef(null);

  const toggle = target =>
    onChange(selected.includes(target) ? selected.filter(t => t !== target) : [...selected, target]);
  const clear = () => onChange([]);
  const close = useCallback(returnFocus => {
    setOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  const count = n => counted(n, noun, nouns);
  const more = moreCount(projects);
  const summary = selectionSummary(projects, selected);

  return (
    <div className="bz-row pd-projects">
      <span className="bz-kicker" style={{ marginRight: 4 }} aria-hidden="true">
        {title}
      </span>
      <div role="group" aria-label={title} className="bz-row" style={{ gap: 6, flexWrap: 'wrap' }}>
        <Chip on={!selected.length} label="All" name={`All ${nouns}`} count={total} onClick={clear} />
        {rowProjects(projects, selected).map(project => (
          <Chip
            key={project.target}
            on={selected.includes(project.target)}
            label={project.name}
            count={project.count}
            onClick={() => toggle(project.target)}
          />
        ))}
        {more > 0 && (
          <div className="pd-more">
            <button
              ref={toggleRef}
              type="button"
              className={`bz-btn bz-btn--sm pd-chip${open ? ' pd-chip--open' : ''}`}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={open ? pickerId : undefined}
              onClick={() => setOpen(value => !value)}
            >
              +{more} more
              <IcoChev size={12} />
            </button>
            {open && (
              <ProjectPicker
                id={pickerId}
                noun={noun}
                nouns={nouns}
                projects={projects}
                selected={selected}
                onToggle={toggle}
                onClear={clear}
                onClose={close}
                toggleRef={toggleRef}
              />
            )}
          </div>
        )}
      </div>
      <div className="bz-grow" />
      {selected.length > 0 && (
        <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }} data-testid={`${testId}-summary`}>
          {count(summary.projects)} · {plural(summary.bugs, 'bug')} ·{' '}
          <button type="button" style={linkButton} onClick={clear}>
            Clear
          </button>
        </span>
      )}
      {aside}
    </div>
  );
};
PredictProjectFilter.propTypes = {
  projects: PropTypes.arrayOf(projectShape).isRequired,
  total: PropTypes.number.isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  aside: PropTypes.node,
  title: PropTypes.string,
  noun: PropTypes.string,
  nouns: PropTypes.string,
  testId: PropTypes.string
};

export default PredictProjectFilter;
